import { useEffect, useState } from "react";
import CustomRadioButton from "../../../../components/customRadioButton/customRadioButton";
import { Question } from "../../types";
import MultiCorrectQuestion from "../multiCorrectQuestion/multiCorrectQuestion";
import SubjectiveQuestion from "../subjectiveQuestion/subjectiveQuestion";
import TabularQuestion from "../tabularQuestions/tabularQuestion";

export interface RecursiveQuestionProps {
  question: Question;
  onAnswerChange: (questionId: string, answer: any) => void;
  currentAnswers: { id: string; answer: any }[];
  depth?: number;
  questionNumber?: string;
}

const RecursiveQuestion = ({
  question,
  onAnswerChange,
  currentAnswers,
  depth = 0,
  questionNumber = "1",
}: RecursiveQuestionProps) => {
  const [singleAnswers, setSingleAnswers] = useState<
    { id: string; answer: string }[]
  >([]);
  const [multiAnswers, setMultiAnswers] = useState<
    { id: string; answer: string[] }[]
  >([]);
  const [subjectiveAnswers, setSubjectiveAnswers] = useState<
    { id: string; answer: string }[]
  >([]);
  const [tabularAnswers, setTabularAnswers] = useState<
    { id: string; answer: any[] }[]
  >([]);

  // Initialize local state from currentAnswers or question.answer
  useEffect(() => {
    const existingAnswer = currentAnswers.find(
      (answer) => answer.id === question.id,
    );

    if (existingAnswer) {
      if (
        question.question_type === "single_correct" &&
        typeof existingAnswer.answer === "string"
      ) {
        setSingleAnswers([{ id: question.id, answer: existingAnswer.answer }]);
      } else if (
        question.question_type === "multi_correct" &&
        Array.isArray(existingAnswer.answer)
      ) {
        setMultiAnswers([{ id: question.id, answer: existingAnswer.answer }]);
      } else if (
        question.question_type === "subjective" &&
        typeof existingAnswer.answer === "string"
      ) {
        setSubjectiveAnswers([
          { id: question.id, answer: existingAnswer.answer },
        ]);
      } else if (
        question.question_type === "tabular" &&
        Array.isArray(existingAnswer.answer)
      ) {
        setTabularAnswers([{ id: question.id, answer: existingAnswer.answer }]);
      }
    } else if (question.answer) {
      // Handle question.answer format (array of objects with id property)
      if (
        question.question_type === "single_correct" &&
        Array.isArray(question.answer) &&
        question.answer.length > 0
      ) {
        const selectedAnswer = question.answer[0];
        if (selectedAnswer && selectedAnswer.id) {
          setSingleAnswers([{ id: question.id, answer: selectedAnswer.id }]);
        }
      }
    }
  }, [question.id, question.question_type, question.answer, currentAnswers]);

  const handleSingleAnswerChange = (questionId: string, answerId: string) => {
    setSingleAnswers((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === questionId);
      if (existingIndex >= 0) {
        const newAnswers = [...prev];
        newAnswers[existingIndex] = { id: questionId, answer: answerId };
        return newAnswers;
      } else {
        const newAnswers = [...prev, { id: questionId, answer: answerId }];
        return newAnswers;
      }
    });
    onAnswerChange(questionId, answerId);
  };

  const handleMultiAnswerChange = (questionId: string, answerIds: string[]) => {
    setMultiAnswers((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === questionId);
      if (existingIndex >= 0) {
        const newAnswers = [...prev];
        newAnswers[existingIndex] = { id: questionId, answer: answerIds };
        return newAnswers;
      } else {
        return [...prev, { id: questionId, answer: answerIds }];
      }
    });
    onAnswerChange(questionId, answerIds);
  };

  const handleSubjectiveAnswerChange = (questionId: string, answer: string) => {
    setSubjectiveAnswers((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === questionId);
      if (existingIndex >= 0) {
        const newAnswers = [...prev];
        newAnswers[existingIndex] = { id: questionId, answer: answer };
        return newAnswers;
      } else {
        return [...prev, { id: questionId, answer: answer }];
      }
    });
    onAnswerChange(questionId, answer);
  };

  const handleTabularAnswerChange = (questionId: string, answer: any[]) => {
    setTabularAnswers((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === questionId);
      if (existingIndex >= 0) {
        const newAnswers = [...prev];
        newAnswers[existingIndex] = { id: questionId, answer: answer };
        return newAnswers;
      } else {
        return [...prev, { id: questionId, answer: answer }];
      }
    });
    onAnswerChange(questionId, answer);
  };

  const getCurrentAnswer = (questionId: string) => {
    const localSingleAnswer = singleAnswers.find(
      (answer) => answer.id === questionId,
    );

    if (localSingleAnswer) return localSingleAnswer.answer;

    const localMultiAnswer = multiAnswers.find(
      (answer) => answer.id === questionId,
    );
    if (localMultiAnswer) return localMultiAnswer.answer;

    const localSubjectiveAnswer = subjectiveAnswers.find(
      (answer) => answer.id === questionId,
    );
    if (localSubjectiveAnswer) return localSubjectiveAnswer.answer;

    const localTabularAnswer = tabularAnswers.find(
      (answer) => answer.id === questionId,
    );
    if (localTabularAnswer) return localTabularAnswer.answer;

    return currentAnswers.find((answer) => answer.id === questionId)?.answer;
  };

  const getChildQuestions = (question: Question) => {
    const currentAnswer = getCurrentAnswer(question.id);

    if (!currentAnswer || !question.children) {
      return [];
    }

    let childQuestions: Question[] = [];

    if (question.question_type === "single_correct") {
      if (question.children[currentAnswer]) {
        childQuestions = question.children[currentAnswer];
      }
    }

    return childQuestions;
  };

  const renderFacts = (facts: any) => {
    if (
      !facts ||
      (!facts.supportive_facts?.length && !facts.contradictory_facts?.length)
    ) {
      return null;
    }

    return (
      <div className="space-y-2">
        {facts.supportive_facts && facts.supportive_facts.length > 0 && (
          <div className="rounded-md bg-green-50 p-2">
            <div className="flex">
              <div className="ml-3">
                <div className="bg-green-50 text-sm font-semibold">
                  Supportive Statement
                </div>
                <div className="text-tiny text-primaryGray-3">
                  <ul className="ml-4 list-outside list-disc space-y-1">
                    {facts.supportive_facts.map(
                      (fact: string, index: number) => (
                        <li key={index}>{fact}</li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {facts.contradictory_facts && facts.contradictory_facts.length > 0 && (
          <div className="rounded-md bg-yellow-50 p-2">
            <div className="flex">
              <div className="ml-3">
                <div className="bg-yellow-50 text-sm font-semibold">
                  Contradictory Statement
                </div>
                <div className="mt-1 text-tiny text-primaryGray-3">
                  <ul className="ml-4 list-outside list-disc space-y-1">
                    {facts.contradictory_facts.map(
                      (fact: string, index: number) => (
                        <li key={index}>{fact}</li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const childQuestions = getChildQuestions(question);

  const renderSingleCorrectQuestionWithChildren = (q: Question) => {
    const currentAnswer = getCurrentAnswer(q.id);

    return (
      <div className="single-correct-question--container">
        <div className="single-correct-question--question text-sm">
          <span className="text-primaryBlue-1 mr-2 font-semibold">
            {questionNumber}.
          </span>
          {q.question}
          {q.required && <span className="required-asterisk">*</span>}
        </div>

        <div className="single-correct-question--options">
          {q.options.map((option) => {
            const isSelected = currentAnswer === option.id;
            const optionChildQuestions = isSelected ? childQuestions : [];

            return (
              <div
                key={option.id}
                className="single-correct-question--option-container"
              >
                <CustomRadioButton
                  name={q.id}
                  value={option.id}
                  checked={isSelected}
                  onChange={() => handleSingleAnswerChange(q.id, option.id)}
                  label={option.text}
                  groupName={q.id}
                  className="single-correct-question--radio-option"
                />

                {isSelected && optionChildQuestions.length > 0 && (
                  <div className="single-correct-question--child-questions ml-6 mt-2 space-y-4">
                    {optionChildQuestions.map((childQuestion, childIndex) => (
                      <RecursiveQuestion
                        key={childQuestion.id}
                        question={childQuestion}
                        onAnswerChange={onAnswerChange}
                        currentAnswers={currentAnswers}
                        depth={depth + 1}
                        questionNumber={`${questionNumber}.${childIndex + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Display facts after options */}
        {renderFacts(q.facts)}
      </div>
    );
  };

  const renderQuestion = (q: Question) => {
    const baseQuestionProps = {
      key: q.id,
      question: {
        id: q.id,
        question: q.question,
        question_type: q.question_type,
        options: q.options || [],
        answer: q.answer,
        children: q.children,
        facts: q.facts,
        required: q.required,
      },
      questionNumber: questionNumber,
    };

    const tabularQuestionProps = {
      ...baseQuestionProps,
      question: {
        ...baseQuestionProps.question,
        table_schema: q.table_schema,
        answer: Array.isArray(q.answer) ? q.answer : [],
      } as any,
    };

    switch (q.question_type) {
      case "single_correct":
        return renderSingleCorrectQuestionWithChildren(q);
      case "multi_correct":
        return (
          <MultiCorrectQuestion
            {...baseQuestionProps}
            onAnswerChange={handleMultiAnswerChange}
            currentAnswers={currentAnswers}
          />
        );
      case "subjective":
        return (
          <SubjectiveQuestion
            {...baseQuestionProps}
            onAnswerChange={handleSubjectiveAnswerChange}
          />
        );
      case "tabular":
        return (
          <TabularQuestion
            {...tabularQuestionProps}
            onAnswerChange={handleTabularAnswerChange}
          />
        );
      default:
        return null;
    }
  };

  const getDepthBackgroundColor = (depth: number) => {
    if (depth === 0) return "bg-white";
    else return "bg-[#F7F9FA]";
  };

  return (
    <div
      className={`recursive-question ${getDepthBackgroundColor(depth)} ${depth > 0 ? "rounded-md p-2 text-sm" : ""}`}
    >
      {renderQuestion(question)}

      {/* Display facts for non-single-correct questions */}
      {question.question_type !== "single_correct" &&
        renderFacts(question.facts)}

      {childQuestions.length > 0 &&
        question.question_type !== "single_correct" && (
          <div
            className={`child-questions flex flex-col gap-2 ${depth > 0 ? "ml-0" : "ml-6"}`}
          >
            {childQuestions.map((childQuestion, childIndex) => (
              <RecursiveQuestion
                key={childQuestion.id}
                question={childQuestion}
                onAnswerChange={onAnswerChange}
                currentAnswers={currentAnswers}
                depth={depth + 1}
                questionNumber={`${questionNumber}.${childIndex + 1}`}
              />
            ))}
          </div>
        )}
    </div>
  );
};

export default RecursiveQuestion;
